import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Select,
  CardHeader,
  MenuItem,
  IconButton,
  OutlinedInput,
  InputAdornment,
  SelectChangeEvent,
  Button,
  Popover,
  Typography,
  Menu,
  TablePagination,
} from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Clear,
  ExpandMore,
  FilterList,
  MoreVert,
  Search,
} from '@mui/icons-material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import InvoiceDialog from './InvoiceDialog';
import { Invoice, InvoiceRow } from '../../../data/models/invoice.model';
import * as invoiceSlice from '../../../data/invoice.slice';
import * as providerSlice from '../../../data/provider.slice';
import { Provider } from '../../../data/models/provider.model';
import React from 'react';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import DateUtils from '../../../services/DateUtils';
import { DateTime } from 'luxon';
import invoiceService from '../../../services/invoiceService';

const statusColors = {
  COLLECTION: {
    backgroundColor: 'rgba(2, 136, 209, 0.12)',
    color: '#01579B',
  },
  'Awaiting Payment': {
    backgroundColor: 'rgba(2, 136, 209, 0.12)',
    color: '#01579B',
  },
  PAID: {
    backgroundColor: 'rgba(46, 125, 50, 0.12)',
    color: '#2E7D32',
  },
};

const PREFIX = 'Invoices';

const classes = {
  container: `${PREFIX}-container`,
  cardMain: `${PREFIX}-cardMain`,
  monitoringCard: `${PREFIX}-monitoringCard`,
  detailCard: `${PREFIX}-detailCard`,
  cardHeader: `${PREFIX}-cardHeader`,
  cardTitle: `${PREFIX}-cardTitle`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  filterButton: `${PREFIX}-filterButton`,
  grid: `${PREFIX}-grid`,
  statusCellContainer: `${PREFIX}-statusCellContainer`,
  statusCell: `${PREFIX}-statusCell`,
  statusText: `${PREFIX}-statusText`,
  statusDate: `${PREFIX}-statusDate`,
  rowHover: `${PREFIX}-rowHover`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.cardMain}`]: {
    width: '100%',
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.monitoringCard}`]: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  [`& .${classes.detailCard}`]: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: 'rgba(1, 16, 18, 0.90)',
    },
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.toolInput}`]: {
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: '#FFF',
    boxShadow: '0px 2px 7px 0px rgba(1, 16, 18, 0.15)',
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    padding: `${theme.spacing(1.5)} 0`,

    '&::placeholder': {
      color: 'rgba(1, 16, 18, 0.60)',
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: theme.spacing(1.5),
    color: '#000',
  },
  [`& .${classes.filterButton}`]: {
    backgroundColor: '#FFF',
    borderRadius: '3px',
    height: '48px',
    padding: '12px 22px',
    color: 'rgba(1, 16, 18, 0.60)',
    fontFamily: 'Source Sans Pro',
    fontSize: '15px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.46px',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    flexGrow: 'unset',
    height: 'fit-content',
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        width: '100%',
        color: 'rgba(1, 16, 18, 0.90)',
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    [`& .${classes.statusCellContainer}`]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
    },
    [`& .${classes.statusCell}`]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 64,
      padding: `${theme.spacing(0.75)} ${theme.spacing(3)}`,
      width: 'fit-content',

      [`& .${classes.statusText}, & .${classes.statusDate}`]: {
        fontFamily: 'Source Sans Pro',
        fontSize: 12,
        fontStyle: 'normal',
        lineHeight: '100%',
        letterSpacing: 0.17,
      },
      [`& .${classes.statusText}`]: {
        fontWeight: 600,
      },
      [`& .${classes.statusDate}`]: {
        fontWeight: 400,
      },
    },
    [`& .MuiDataGrid-row`]: {
      letterSpacing: '0.17px',
      cursor: 'pointer',
      '& .MuiDataGrid-cell:last-child': {
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
    [`& .MuiDataGrid-row:not(:hover):not(.${classes.rowHover})`]: {
      '& .MuiDataGrid-cell:last-child > div': {
        position: 'absolute',
        right: 0,
        opacity: 0,
      },
    },
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  [`& .MuiPaper-root`]: {
    width: '160px',
    marginLeft: theme.spacing(1.25),
    borderRadius: '3px',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

    [`& .MuiList-root`]: {
      padding: 0,
    },
    [`& .MuiMenuItem-root`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
      padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    },
  },
}));

export default function Invoices() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [providerId, setProviderId] = useState<number>();

  const [searchQuery, setSearchQuery] = useState('');
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('All Invoices');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [invoice, setInvoice] = useState<InvoiceRow | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuOpenRowId, setMenuOpenRowId] = useState<number | null>(null);

  const loggedInProviderId = useLoggedInProviderId();
  const bensonProviderId = useAppSelector(providerSlice.selectBensonProviderID);

  useEffect(() => {
    if (!loggedInProviderId) {
      dispatch(providerSlice.fetchBensonProviderIDAsync());
    }
  }, [loggedInProviderId]);

  useEffect(() => {
    if (loggedInProviderId) {
      setProviderId(loggedInProviderId);
    } else if (bensonProviderId) {
      setProviderId(bensonProviderId);
    }
  }, [loggedInProviderId, bensonProviderId]);

  const loading = useAppSelector(providerSlice.selectLoadingProvider);
  const loadingError = useAppSelector(providerSlice.selectLoadingProviderError);
  const provider = useAppSelector(providerSlice.selectProvider) as Provider;

  React.useEffect(() => {
    if (providerId) {
      dispatch(invoiceSlice.fetchInvoicesAsync(providerId));
    }
  }, [providerId]);

  const invoices = useAppSelector(invoiceSlice.selectInvoices);
  const invoiceList: InvoiceRow[] = invoices.invoices.map((invoice) => ({
    id: invoice.providerInvoiceID,
    invoiceNumber: invoice.invoiceNumber,
    amount: invoice.invoiceAmount,
    paymentOn: invoice.invoiceDate
      ? DateUtils.formatDate(DateTime.fromISO(invoice.invoiceDate))
      : '',
    createdOn: invoice.createdOnUTC
      ? DateUtils.formatDate(DateTime.fromISO(invoice.createdOnUTC))
      : '',
    totalTripLegs: invoice.totalTripLegs,
    billingStatusID: invoice.billingStatusID,
    billingStatusBC: invoice.billingStatusBC,
  }));

  let filteredInvoices: InvoiceRow[] = useMemo(() => {
    const normalizedSearch = searchQuery.toLowerCase();
    return invoiceList.filter((invoice) => {
      const matchesSearch = [
        invoice.id,
        invoice.invoiceNumber,
        invoice.amount,
        invoice.createdOn,
        invoice.paymentOn,
        invoice.totalTripLegs,
        invoice.billingStatusBC,
      ].some(
        (field) => field?.toString().toLowerCase().includes(normalizedSearch),
      );

      const matchesInvoiceType =
        invoiceTypeFilter === t('invoices.allInvoices') ||
        invoice.billingStatusBC === invoiceTypeFilter;

      return matchesSearch && matchesInvoiceType;
    });
  }, [invoiceList, searchQuery, invoiceTypeFilter]);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedInvoiceList = filteredInvoices.slice(startIndex, endIndex);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    [setSearchQuery],
  );

  const handleInvoiceTypeFilterChange = useCallback(
    (event: SelectChangeEvent) => {
      setInvoiceTypeFilter(event.target.value);
    },
    [setInvoiceTypeFilter],
  );

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterOpen(true);
    setFilterAnchorEl(event.currentTarget);
  };

  const timezoneOffset = DateTime.now().offset;

  const handleDownloadInvoice = (
    providerInvoiceID: number,
    invoiceNumber: string,
  ) => {
    invoiceService
      .generateInvoicePDF(providerInvoiceID, timezoneOffset)
      .subscribe((buffer) => {
        const blob = new Blob([buffer], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${invoiceNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
    setFilterAnchorEl(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuOpen(true);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    setMenuAnchorEl(null);
  };

  const openMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: number,
    row: InvoiceRow,
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuOpenRowId(rowId);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuOpenRowId(null);
  };
  const columns: GridColDef<InvoiceRow>[] = [
    {
      field: 'invoiceNumber',
      headerName: 'ID',
      width: 200,
      renderCell: (params) => {
        return <Box>{params.row.invoiceNumber}</Box>;
      },
    },
    { field: 'createdOn', headerName: 'Generated At', width: 140 },
    {
      field: 'totalTripLegs',
      headerName: 'Trips',
      width: 64,
      align: 'right',
      renderHeader: () => (
        <Typography align="right">{t('invoices.trips')}</Typography>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 143,
      align: 'right',
      renderHeader: () => (
        <Typography align="right">{t('invoices.amount')}</Typography>
      ),
      renderCell: (params) =>
        `${params.value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 330,
      align: 'center',
      renderHeader: () => (
        <Typography align="center">{t('invoices.status')}</Typography>
      ),
      renderCell: (params) => (
        <Box className={classes.statusCellContainer}>
          {params.row.billingStatusID && (
            <Box
              className={classes.statusCell}
              sx={
                statusColors[
                  params.row.billingStatusBC as keyof typeof statusColors
                ]
              }>
              <Typography className={classes.statusText}>
                {params.row.billingStatusBC}
              </Typography>
              <Typography className={classes.statusDate}>
                {params.row.paymentOn}
              </Typography>
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: ' ',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box
          onClick={(e) => {
            e.stopPropagation();
          }}>
          <IconButton onClick={(e) => openMenu(e, params.row.id, params.row)}>
            <MoreVert />
          </IconButton>
          <StyledMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && menuOpenRowId === params.row.id}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={closeMenu}
            elevation={1}>
            <MenuItem
              onClick={() => {
                handleDownloadInvoice(params.row.id, params.row.invoiceNumber);
                closeMenu();
              }}>
              {t('invoices.download')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
              }}>
              {t('invoices.share')}
            </MenuItem>
          </StyledMenu>
        </Box>
      ),
    },
  ];

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Card className={classes.cardMain}>
        <CardHeader
          title={`${t('common.invoices')}`}
          className={classes.cardHeader}
        />
        <CardContent>
          <Box className={classes.toolbar}>
            <Box className={classes.toolContainer}>
              <OutlinedInput
                size="small"
                className={classes.toolInput}
                sx={{ width: '327px' }}
                placeholder={t('invoices.search')}
                value={searchQuery}
                onChange={handleSearchChange}
                startAdornment={
                  <InputAdornment position="start">
                    {searchQuery ? (
                      <IconButton
                        edge="start"
                        onClick={() => setSearchQuery('')}>
                        <Clear />
                      </IconButton>
                    ) : (
                      <IconButton edge="start">
                        <Search />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />
              <Select
                className={classes.toolInput}
                defaultValue={t('invoices.allInvoices')}
                sx={{ width: '180px' }}
                IconComponent={ExpandMore}
                value={invoiceTypeFilter}
                onChange={handleInvoiceTypeFilterChange}>
                <MenuItem value={t('invoices.allInvoices')}>
                  {t('invoices.allInvoices')}
                </MenuItem>
                <MenuItem value={t('invoices.processingPayment')}>
                  {t('invoices.processingPayment')}
                </MenuItem>
                <MenuItem value={t('invoices.awaitingPayment')}>
                  {t('invoices.awaitingPayment')}
                </MenuItem>
                <MenuItem value={t('invoices.paid')}>
                  {t('invoices.paid')}
                </MenuItem>
              </Select>
              <Button
                variant="contained"
                onClick={handleFilterClick}
                className={classes.filterButton}
                startIcon={<FilterList />}>
                {t('common.filter')}
              </Button>
              <Popover
                open={filterOpen}
                anchorEl={filterAnchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}>
                <Box p={10}>
                  <Typography>{t('common.filter')}</Typography>
                </Box>
              </Popover>
            </Box>
            {/* TODO: Temporary hide menu till requirements are clear */}
            {/* <Box>
              <IconButton onClick={handleMenuClick}>
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}>
                <MenuItem onClick={handleMenuClose}>1</MenuItem>
                <MenuItem onClick={handleMenuClose}>2</MenuItem>
                <MenuItem onClick={handleMenuClose}>3</MenuItem>
              </Menu>
            </Box> */}
          </Box>
          <DataGridPro
            rows={paginatedInvoiceList}
            getRowId={(row) => row.id}
            columns={columns as GridColDef<InvoiceRow>[]}
            className={classes.grid}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            hideFooterRowCount
            hideFooter
            rowHeight={56}
            disableColumnMenu={true}
            disableColumnSorting={true}
            disableColumnResize={true}
            getRowClassName={(params) => {
              if (params.row.id === menuOpenRowId) {
                return classes.rowHover;
              }
              return '';
            }}
            onCellClick={(params, event) => {
              setInvoice(params.row);
            }}
          />
          <TablePagination
            component="div"
            count={invoiceList.length}
            page={page - 1}
            onPageChange={(event, page) => setPage(page + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(Number.parseInt(event.target.value, 10));
              setPage(1);
            }}
            labelRowsPerPage=""
          />
        </CardContent>
      </Card>
      <InvoiceDialog
        open={!!invoice}
        invoice={invoice as InvoiceRow}
        provider={provider}
        onDownloadInvoice={handleDownloadInvoice}
        onClose={() => setInvoice(null)}
      />
    </StyledBox>
  );
}
